import React, { useState } from 'react';
import { 
  BookOpen, Lock, User, Key, ArrowRight, CheckCircle2, 
  AlertCircle, Sparkles, ArrowLeft, Eye, EyeOff, GraduationCap,
  Award, Building2, Calendar, ChevronRight, School, Briefcase, Users
} from 'lucide-react';
import { AUTH_CREDENTIALS, USER_ROLES } from '../../../data/lmsAuthData';
import { FACULTY_LECTURERS, SEMESTER_COURSES } from '../../../data/semesterCourseData';

export const FacultyAuthPage = ({ onLoginSuccess, setActiveView }) => {
  // Available faculty list derived from master semester data
  const allFaculty = FACULTY_LECTURERS;
  const danielFaculty = allFaculty.find(f => f.id === 'fac-daniel') || allFaculty[1];
  const [selectedProgrammeFilter, setSelectedProgrammeFilter] = useState('all'); // 'all' | 'dba' | 'maf' | 'daf'
  const [selectedLecturerId, setSelectedLecturerId] = useState(allFaculty[0].id);

  const selectedLecturer = allFaculty.find(f => f.id === selectedLecturerId) || allFaculty[0];

  const [username, setUsername] = useState(selectedLecturer.demoUsername);
  const [password, setPassword] = useState(selectedLecturer.demoPassword);
  const [department, setDepartment] = useState(selectedLecturer.department);
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Filtered faculty for fast-pass tabs
  const filteredFaculty = allFaculty.filter(fac => {
    if (selectedProgrammeFilter === 'all') return true;
    if (selectedProgrammeFilter === 'dba') {
      return fac.assignedCourses.some(c => c.startsWith('BUS'));
    }
    if (selectedProgrammeFilter === 'maf') {
      return fac.assignedCourses.some(c => c.startsWith('MAF') || c.startsWith('ACC 8') || c.startsWith('FIN 8'));
    }
    if (selectedProgrammeFilter === 'daf') {
      return fac.assignedCourses.some(c => c.startsWith('DAF') || c.startsWith('ACC 9') || c.startsWith('FIN 9'));
    }
    return true;
  });

  // Build full auth object for selected lecturer
  const createFacultyAuthObject = (fac) => {
    const cred = AUTH_CREDENTIALS.find(c => c.facultyId === fac.id) || {
      id: `user-${fac.id}`,
      facultyId: fac.id,
      role: USER_ROLES.FACULTY,
      label: `${fac.name} (${fac.assignedCourses.join(' & ')})`,
      name: fac.name,
      formalSalutation: fac.formalSalutation,
      title: fac.title,
      rank: fac.rank,
      qualifications: fac.qualifications,
      email: fac.email,
      demoUsername: fac.demoUsername,
      demoPassword: fac.demoPassword,
      department: fac.department,
      badge: `COURSE CHAIR · ${fac.assignedCourses.join(' & ')}`,
      leastPrivilegeTier: `Tier 2: Course-Scoped Academic Authority (${fac.assignedCourses.join(', ')})`,
      assignedCourses: fac.assignedCourses,
      avatar: fac.avatar,
      phone: fac.phone,
      office: fac.office,
      consultationHours: fac.consultationHours,
      bio: fac.bio,
      researchInterests: fac.researchInterests,
      workingPapers: fac.workingPapers
    };
    return cred;
  };

  const handleSelectLecturer = (fac) => {
    setSelectedLecturerId(fac.id);
    setUsername(fac.demoUsername);
    setPassword(fac.demoPassword);
    setDepartment(fac.department);
    setAuthError('');
  };

  const handle1ClickFastPass = (fac = selectedLecturer) => {
    setUsername(fac.demoUsername);
    setPassword(fac.demoPassword);
    setDepartment(fac.department);
    setAuthError('');
    const authObj = createFacultyAuthObject(fac);
    onLoginSuccess(authObj);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setAuthError('');

    setTimeout(() => {
      setIsAuthenticating(false);
      const cleanUser = username.trim().toLowerCase();

      // Find match among all 11 lecturers
      const matchedFaculty = allFaculty.find(f => 
        f.demoUsername.toLowerCase() === cleanUser ||
        f.email.toLowerCase() === cleanUser ||
        f.name.toLowerCase().includes(cleanUser) ||
        f.id.toLowerCase() === cleanUser
      );

      if (matchedFaculty && (password === matchedFaculty.demoPassword || password.length >= 4)) {
        const authObj = createFacultyAuthObject(matchedFaculty);
        onLoginSuccess(authObj);
      } else {
        // Fallback match to selected lecturer if password meets length
        if (password.length >= 4 && selectedLecturer) {
          const authObj = createFacultyAuthObject(selectedLecturer);
          onLoginSuccess(authObj);
        } else {
          setAuthError('Faculty clearance failed. Please select any lecturer from the 1-Click Fast Pass grid or verify your staff email and password.');
        }
      }
    }, 400);
  };

  return (
    <div className="min-h-screen bg-[#030F1C] text-slate-100 flex flex-col justify-between relative overflow-hidden py-6 sm:py-8">
      {/* Warm Amber Academic Glow */}
      <div className="absolute top-10 left-1/3 w-96 h-96 bg-amber-600/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#0B2644]/50 rounded-full blur-3xl pointer-events-none" />

      {/* Top Breadcrumb Nav */}
      <div className="max-w-6xl mx-auto px-4 w-full mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <button
            onClick={() => setActiveView('lms-hub')}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-[#0B2644] hover:bg-[#7A470E] text-white text-xs font-bold font-sc transition-colors border border-slate-700/80 cursor-pointer shadow-xs"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-amber-300" />
            <span>← Back to Central Portals</span>
          </button>

          <div className="flex items-center space-x-2 text-xs font-mono bg-amber-950/80 border border-amber-600/40 text-amber-300 px-3 py-1.5 rounded-full">
            <BookOpen className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">Tier-2 Course-Scoped Academic Authority · 11 Assigned Chairs</span>
          </div>
        </div>
      </div>

      {/* Main Authentication Card */}
      <div className="max-w-6xl mx-auto px-4 w-full flex-1 flex flex-col justify-center">
        <div className="bg-gradient-to-b from-[#082038] via-[#06182B] to-[#04101D] border-2 border-amber-500/40 rounded-3xl p-5 sm:p-8 md:p-10 shadow-2xl">
          
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto space-y-2.5 mb-8">
            <div className="inline-flex items-center justify-center p-3.5 bg-gradient-to-br from-[#7A470E] to-[#0B2644] rounded-2xl border-2 border-amber-400 text-amber-200 shadow-xl">
              <BookOpen className="w-8 h-8 sm:w-10 sm:h-10" />
            </div>
            <div>
              <span className="text-[11px] sm:text-xs font-mono font-bold uppercase tracking-widest text-amber-400">
                Academic Faculty & Executive Course Chairs
              </span>
              <h1 className="text-2xl sm:text-4xl font-black font-sc text-white tracking-wide mt-1">
                Executive Faculty Portal
              </h1>
              <p className="text-xs sm:text-sm text-slate-300 font-light mt-1">
                Dedicated workspace for all 11 course lecturers across DBA, MAF, and DAF semester tracks.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
            
            {/* Left Column: Form & Selected Lecturer Details */}
            <div className="lg:col-span-5 bg-[#04101D]/80 p-5 sm:p-6 rounded-2xl border border-slate-800 space-y-4">
              
              {/* Selected Lecturer Card */}
              <div className="p-3.5 rounded-xl bg-gradient-to-r from-[#0B2644] to-[#06182B] border border-amber-400/40 flex items-center space-x-3">
                <img 
                  src={selectedLecturer.avatar} 
                  alt={selectedLecturer.name} 
                  className="w-12 h-12 rounded-xl object-cover border-2 border-amber-400 shrink-0" 
                />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase font-bold text-amber-400 font-mono tracking-wider">
                    Active Clearance Selection
                  </div>
                  <div className="text-sm font-bold text-white font-sc truncate">
                    {selectedLecturer.name}
                  </div>
                  <div className="text-[11px] text-amber-200/90 font-mono truncate">
                    {selectedLecturer.assignedCourses.join(' & ')} · {selectedLecturer.department}
                  </div>
                </div>
              </div>

              <form onSubmit={handleFormSubmit} className="space-y-3.5">
                {authError && (
                  <div className="p-3 bg-red-950/80 border border-red-500/50 rounded-xl text-xs text-red-200 flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <span>{authError}</span>
                  </div>
                )}

                {/* Faculty Email / Staff Username */}
                <div>
                  <label className="block text-xs font-bold font-sc text-slate-200 mb-1">
                    Staff Email / Access ID:
                  </label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="prof.uford@businessschool.aksu.edu.ng"
                      required
                      className="w-full pl-10 pr-4 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold font-sc text-slate-200">
                      Faculty Academic Password:
                    </label>
                    <span className="text-[10px] text-amber-300/80 font-mono">Scoped Workspace</span>
                  </div>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••••"
                      required
                      className="w-full pl-10 pr-10 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white cursor-pointer"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Assigned Academic Department */}
                <div>
                  <label className="block text-xs font-bold font-sc text-slate-200 mb-1">
                    Assigned Academic Department:
                  </label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <select
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-[#06182B] border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:border-amber-400"
                    >
                      <option value="Department of Business Administration">Department of Business Administration</option>
                      <option value="Department of Accounting">Department of Accounting</option>
                      <option value="Department of Banking & Finance">Department of Banking & Finance</option>
                      <option value="Department of Marketing">Department of Marketing</option>
                      <option value="Department of Public Administration">Department of Public Administration</option>
                    </select>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isAuthenticating}
                    className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#7A470E] via-[#5C3306] to-[#0B2644] hover:from-[#5C3306] hover:to-[#06182B] text-white font-sc font-bold text-xs flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transition-all cursor-pointer disabled:opacity-50"
                  >
                    <span>{isAuthenticating ? 'Authorizing Academic Desk...' : `Enter ${selectedLecturer.name.split(' ')[1] || 'Faculty'}'s Workspace`}</span>
                    <ArrowRight className="w-4 h-4 text-amber-200" />
                  </button>
                </div>
              </form>

              {/* Security info */}
              <div className="p-3 rounded-xl bg-[#06182B] border border-slate-800 text-[11px] text-slate-400 space-y-1">
                <div className="flex items-center space-x-1.5 text-amber-300 font-bold font-sc">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>Scoped Role-Based Access Control</span>
                </div>
                <p className="text-[10px] text-slate-500">
                  Each lecturer workspace is bound strictly to assigned courses ({selectedLecturer.assignedCourses.join(', ')}). Materials, grading, and messaging are isolated per chair.
                </p>
              </div>

            </div>

            {/* Right Column: 11 Lecturers 1-Click Fast Pass Grid */}
            <div className="lg:col-span-7 space-y-4">
              
              <div className="bg-[#0B2644]/40 p-4 sm:p-5 rounded-2xl border border-amber-400/40 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center space-x-2 text-xs font-bold font-sc text-amber-300">
                    <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
                    <span>Evaluator 1-Click Fast Pass (11 Lecturers):</span>
                  </div>
                  <span className="text-[10px] font-mono text-slate-400">
                    Click any lecturer to enter workspace
                  </span>
                </div>

                {/* Spotlight Fast-Pass Card for Dr. Ekere Jacob Daniel */}
                {danielFaculty && (
                  <div className="bg-gradient-to-r from-[#030F1C] via-[#0B2644] to-[#7A470E]/50 p-3.5 sm:p-4 rounded-2xl border-2 border-amber-400 shadow-xl space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center space-x-3">
                        <div className="relative shrink-0">
                          <img 
                            src={danielFaculty.avatar || "/dr-daniel-ekere.png"} 
                            alt={danielFaculty.name} 
                            className="w-13 h-13 sm:w-14 sm:h-14 rounded-2xl object-cover object-top border-2 border-amber-400 shadow-md bg-slate-800" 
                          />
                          <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-slate-900" title="Active Lecturer" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-0.5 rounded bg-amber-400 text-[#0B2644] font-black text-[10px] font-sc">
                              VIP SPOTLIGHT CHAIR
                            </span>
                            <span className="text-[10px] font-mono text-emerald-300 font-bold">
                              BUS/DAF 911 Ready
                            </span>
                          </div>
                          <h4 className="text-sm font-black font-sc text-white mt-0.5">
                            {danielFaculty.name}
                          </h4>
                          <p className="text-[11px] text-amber-200/90 font-mono">
                            BUS 911 & DAF 911 · {danielFaculty.department}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handle1ClickFastPass(danielFaculty)}
                        className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-[#7A470E] hover:from-amber-400 hover:to-[#5C3306] text-white font-sc font-black text-xs shadow-lg transition-all hover:scale-105 cursor-pointer shrink-0 flex items-center justify-center space-x-1.5"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-amber-200" />
                        <span>1-Click Fast Pass</span>
                      </button>
                    </div>

                    <div className="bg-[#04101D]/90 p-2.5 rounded-xl border border-slate-800 flex flex-wrap items-center justify-between gap-2 text-[11px] font-mono">
                      <div>
                        <span className="text-slate-400">Username: </span>
                        <strong className="text-amber-300">{danielFaculty.demoUsername}</strong>
                      </div>
                      <div>
                        <span className="text-slate-400">Password: </span>
                        <strong className="text-emerald-400">{danielFaculty.demoPassword}</strong>
                      </div>
                    </div>
                  </div>
                )}

                {/* Filter Tabs by Programme */}
                <div className="flex flex-wrap gap-1.5 p-1 bg-[#04101D] rounded-xl border border-slate-800">
                  <button
                    type="button"
                    onClick={() => setSelectedProgrammeFilter('all')}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold font-sc transition-colors cursor-pointer ${
                      selectedProgrammeFilter === 'all' 
                        ? 'bg-[#7A470E] text-white shadow-xs' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    All Chairs (11)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedProgrammeFilter('dba')}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold font-sc transition-colors cursor-pointer ${
                      selectedProgrammeFilter === 'dba' 
                        ? 'bg-[#7A470E] text-white shadow-xs' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    DBA (5 Courses)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedProgrammeFilter('maf')}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold font-sc transition-colors cursor-pointer ${
                      selectedProgrammeFilter === 'maf' 
                        ? 'bg-[#7A470E] text-white shadow-xs' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    MAF (5 Courses)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedProgrammeFilter('daf')}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold font-sc transition-colors cursor-pointer ${
                      selectedProgrammeFilter === 'daf' 
                        ? 'bg-[#7A470E] text-white shadow-xs' 
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    DAF (5 Courses)
                  </button>
                </div>

                {/* Lecturer Fast Pass List */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[440px] overflow-y-auto pr-1">
                  {filteredFaculty.map((fac) => {
                    const isSelected = selectedLecturerId === fac.id;
                    return (
                      <div
                        key={fac.id}
                        className={`p-2.5 rounded-xl border transition-all text-left flex items-center justify-between group cursor-pointer ${
                          isSelected 
                            ? 'bg-gradient-to-r from-[#7A470E]/70 to-[#0B2644] border-amber-400 shadow-md ring-1 ring-amber-400' 
                            : 'bg-[#06182B] hover:bg-[#08233D] border-slate-700/80 hover:border-amber-400/60'
                        }`}
                        onClick={() => handleSelectLecturer(fac)}
                      >
                        <div className="flex items-center space-x-2.5 min-w-0">
                          <img 
                            src={fac.avatar} 
                            alt={fac.name} 
                            className="w-10 h-10 rounded-lg object-cover border border-amber-400/80 shrink-0" 
                          />
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-white group-hover:text-amber-200 font-sc truncate">
                              {fac.name}
                            </div>
                            <div className="text-[10px] text-amber-300 font-mono font-bold truncate">
                              {fac.assignedCourses.join(' & ')}
                            </div>
                            <div className="text-[9px] text-slate-400 truncate">
                              {fac.rank}
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handle1ClickFastPass(fac);
                          }}
                          className="shrink-0 p-1.5 rounded-lg bg-amber-500/20 hover:bg-[#7A470E] text-amber-300 hover:text-white border border-amber-400/40 transition-colors ml-2 cursor-pointer"
                          title={`Log in as ${fac.name}`}
                        >
                          <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Bottom Quick Fast-Pass Enter CTA */}
                <button
                  type="button"
                  onClick={() => handle1ClickFastPass(selectedLecturer)}
                  className="w-full py-2.5 px-3 rounded-xl bg-gradient-to-r from-[#7A470E] to-[#0B2644] hover:from-[#5C3306] hover:to-[#06182B] text-white text-xs font-bold font-sc flex items-center justify-center space-x-2 border border-amber-400/50 shadow-md cursor-pointer transition-all"
                >
                  <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                  <span>1-Click Fast Pass: Enter as {selectedLecturer.name} ({selectedLecturer.assignedCourses.join(' & ')})</span>
                </button>
              </div>

            </div>

          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="max-w-6xl mx-auto px-4 w-full mt-6 text-center text-xs text-slate-400">
        <span className="font-mono text-[11px]">
          Akwa Ibom State University Executive Business School · Directorate of Academic Faculty · 11 Dedicated Workspaces
        </span>
      </div>
    </div>
  );
};

export default FacultyAuthPage;
